import React from 'react';
import { AxiosResponse } from 'axios';
import { WEATHER_PAGE_SIZE } from 'ots-constants';
import { SidebarAd } from 'googlead/components/SidebarAd';
import { useMustacheTranslation, useOTSTranslation } from 'i18n/useOTSTranslation';
import {
  fetchRegionClosingDate,
  fetchRegionFeatured,
  fetchRegionMeta, fetchRegionRelated, fetchRegionRelatedNews,
  fetchRegionResortsByStatus,
} from 'api/region/static';
import { IRegionResortsPaginated, IRegionShort } from 'api/region/types';
import { IRelatedNews, ResortStatus } from 'api/resort/types';
import { CurrentLink, CurrentTab } from 'components/region/RegionHeaderNav/constants';
import { WeatherReports } from 'components/region/WeatherReports';
import { BoxWithShadow } from 'components/blocks/BoxWithShadow';
import { HeaderNavCurLink } from 'components/common/HeaderNav';
import { BlockContent } from 'components/blocks/BlockContent';
import { BlockHeader } from 'components/blocks/BlockHeader';
import { Main } from 'components/resort/Layout/Main';
import { Side } from 'components/resort/Layout/Side';
import { useRegionLayoutBubble } from 'page-components/region/_context';
import { IRegionLayout } from 'page-components/region/_layout';
import { RouteAndAnalyticsProps } from 'page-components/pathTypes';
import { getRegionSlug } from 'util/memcache';

import PlanYourTrip from 'components/common/PlanYourTrip/RegionWidget';
import { Hero400Error } from 'components/400';
import NoResortsBlock from 'components/blocks/NoResortBlock';
import { formatDateFromStr, MINIMAL_DATE_FORMAT } from 'util/formatDate';
import SurfaceRelatedArticles from 'components/resort/SurfaceRelatedArticles';
import { assertShouldBeNot404, wrapServerSideProps, wrapStaticProps } from 'util/wrapProps';

export type RegionProjectedClosingPageProps = {
  resorts: IRegionResortsPaginated,
  closingDate: string,
  pathInfo: IRegionShort,
  numResorts: number,
  relatedNews: IRelatedNews | null,
} & RouteAndAnalyticsProps & IRegionLayout;

export const DEBUG_TAG = 'region-projected-closing-page';
export const TEMPLATE_ID = '2.1.5';
export const TEMPLATE_NAME = 'Region Projected Closing';

export default function RegionProjectedClosingPage({
  closingDate, numResorts, resorts, pathInfo,
  relatedNews,
}: RegionProjectedClosingPageProps) {
  useRegionLayoutBubble({});
  const { t } = useOTSTranslation('common');
  const m = useMustacheTranslation('common');

  if (!resorts) {
    return <Hero400Error light />;
  }

  const seoTitle = pathInfo.isLogical ? t('region.closingDates.logicSeoTitle', { region: pathInfo.title })
    : t('region.closingDates.seoTitle', { region: pathInfo.title });

  const seoText = pathInfo.isLogical ? m('region.closingDates.logicSeoText', {
    region: pathInfo.title,
    numresorts: numResorts,
    lastclosedate: formatDateFromStr(closingDate, MINIMAL_DATE_FORMAT),
  })
    : m('region.closingDates.seoText', {
      region: pathInfo.title,
      numresorts: numResorts,
      lastclosedate: formatDateFromStr(closingDate, MINIMAL_DATE_FORMAT),
    });

  return (
    <>
      <Main>
        <BoxWithShadow>
          <BlockHeader title={seoTitle} />
          <BlockContent>
            {seoText}
          </BlockContent>
        </BoxWithShadow>
        {!!resorts.pagination?.count && (
          <WeatherReports
            regionId={pathInfo.uuid}
            resorts={resorts}
            openFlag={ResortStatus.projectedClosing}
            isFresh
          />
        )}
        {!resorts.pagination?.count && (
          <NoResortsBlock text={t('region.closingDates.noResorts', { region: pathInfo.title })} />
        )}
        <PlanYourTrip
          region={pathInfo}
          hasSkiRental={false}
          hasCarRental={false}
          hasFlights={false}
          isPartner={false}
        />
      </Main>
      <Side>
        <SurfaceRelatedArticles
          relatedNewsList={relatedNews}
          isFresh
        />
        <SidebarAd isRegion id={pathInfo.uuid} showWeSki={false} />
      </Side>
    </>
  );
}

export const fetchProps = async ({ region }: { region: string }): Promise<RegionProjectedClosingPageProps> => {
  const cache: AxiosResponse<IRegionShort> = await assertShouldBeNot404(() => getRegionSlug(region));
  const { uuid } = cache.data;
  const featured = await fetchRegionFeatured(uuid);

  return {
    type: 'region',
    route: 'projected-closing',
    resorts: await fetchRegionResortsByStatus(uuid, ResortStatus.projectedClosing, 0, WEATHER_PAGE_SIZE),
    closingDate: await fetchRegionClosingDate(uuid),
    featured,
    pathInfo: cache.data,
    numResorts: (await fetchRegionMeta(uuid))?.resorts?.active,
    relatedNews: await fetchRegionRelatedNews(uuid),

    /**
       * Layout
       */
    related: await fetchRegionRelated(uuid),
    navigation: {
      head: HeaderNavCurLink.SNOW_REPORTS,
      tab: CurrentTab.TRIP_PLANNING,
      link: CurrentLink.PROJECTED_CLOSING,
    },
    adTKey: 'metaregion projected-closing',
    templateName: TEMPLATE_NAME,
    templateId: TEMPLATE_ID,
    hero: {
      title: 'region.closingDates.heroTitle',
    },
    meta: {
      redirectTo: cache.data.domain ?? null,
      description: 'MetaRegion.projected_closing.description',
      title: 'MetaRegion.projected_closing.title',
    },
    prefetch: false,
  };
};

// This function gets called at build time
// eslint-disable-next-line max-len
export const getRegionProjectedClosingPageStaticProps = wrapStaticProps(fetchProps);

export const getRegionProjectedClosingPageServerSideProps = wrapServerSideProps(fetchProps);
